import classNames from 'classnames';
import { type ComponentProps, useCallback } from 'react';
import Check from '../../icons/Check';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import Tooltip from '../Tooltip';
import styles from './styles/styles.module.scss';

export type ChipTooltip = Pick<ComponentProps<typeof Tooltip>, 'text' | 'uniqueId' | 'maxWidth'>;

export type Chip = {
  id: string; // untranslated label
  label: string; // translated label
  icon?: IconDefinition<'orientation'>;
  className?: string;
  tooltip?: ChipTooltip;
};

type SelectionChip = {
  /* When true, allows clicking to de-select when multi-select is false */
  allowDeselect?: boolean;
  isMultiSelect?: boolean;
  leadingIcon?: IconDefinition<'orientation'>;
  isSelected?: boolean;
  chip: Chip;
  name: string;
  onClick: (chip: Chip) => void;
  onDoubleClick?: (chip: Chip) => void;
};

const SelectionChip = ({ allowDeselect, isMultiSelect, chip, leadingIcon, isSelected, name, onClick, onDoubleClick }: SelectionChip) => {
  const handleClick = useCallback(() => onClick(chip), [chip, onClick]);
  const handleDoubleClick = useCallback(() => onDoubleClick?.(chip), [chip, onDoubleClick]);

  const chipBody = (
    <div
      className={classNames(styles.chip, styles.chipDefaultHover, chip.className, {
        [styles.selected]: isSelected,
        [styles.fillSelected]: isSelected && !isMultiSelect
      })}
      data-testid={chip.id}
    >
      <label className={styles.optionLabel}>
        <input
          className={styles.input}
          type={isMultiSelect || allowDeselect ? 'checkbox' : 'radio'}
          name={name}
          value={chip.id}
          onChange={handleClick}
          onDoubleClick={onDoubleClick ? handleDoubleClick : undefined}
          checked={isSelected}
          aria-label={chip.label}
          data-tooltip-id={chip.tooltip?.uniqueId}
        />
        {leadingIcon ? (
          <div className={styles.marginRight}>
            <IconRenderer icon={leadingIcon} defaults={{}} />
          </div>
        ) : null}
        <span className={styles.textContent}>{chip.label}</span>
        {isMultiSelect && (
          <div
            className={classNames(styles.hideCheckmark, {
              [styles.showCheckmark]: isSelected
            })}
          >
            {isSelected && <IconRenderer icon={{ Component: Check }} defaults={{}} />}
          </div>
        )}
      </label>
    </div>
  );

  if (chip.tooltip?.uniqueId) {
    return (
      <Tooltip
        anchorElement={chipBody}
        anchorHandlesFocus
        text={chip.tooltip.text}
        uniqueId={chip.tooltip.uniqueId}
        maxWidth={chip.tooltip.maxWidth}
        popperClassName={styles.selectionChipTooltipPopper}
      />
    );
  }

  return chipBody;
};

export default SelectionChip;
